import {useLocale, useMessages} from "i18n";
import {PeelTicket} from "peel/PeelTicket";
import {formatMoney} from "pricing/PricingStorage";
import {firstMonthPresentation} from "pricing/welcomePrice";
import type {CouponGift} from "./currentLadderOffer";
import type {CouponCountdown} from "./couponCountdown";

/**
 * Only deadline-based entries supply a countdown; scarcity always comes from their server answer.
 *
 * The print carries the FIRST-MONTH AMOUNT, not the coupon's percentage or amount off
 * (kitecorp/transferify-ui#2545): the ticket states the prices the reader will pay, the presentation
 * the owner settled on ui#2441 and ui#2456; whether "first month X, then Y" counts as a reduction announcement under OG 99/2000 art. 33 is the open counsel question recorded on ui#2456. The renewal price rides in the heading's
 * accessible name, because the ticket has room for one figure; the claim dialog states both in full.
 *
 * The Halloween ticket (`gift.intro`, kitecorp/transferify-ui#3776) prints the same one figure under "30 days"
 * instead of "First month", its heading names the plan and both of the api's prices, and its metadata line is
 * empty: it has no daily count, and the owner's design carries no countdown (2026-10-26).
 */
export function CouponTicket({gift, countdown, discountsLeft, claimed = false, onClaim, stub = true, eyebrow, endsOn}: {
	gift: CouponGift; countdown?: CouponCountdown; discountsLeft?: number; claimed?: boolean; onClaim?: () => void; stub?: boolean;
	/** Forwarded to the ticket print — the promo dialog names its platform here. */
	eyebrow?: string;
	/**
	 * The weekday the offer ends, for an entry with a deadline but no countdown: the limit-wall email's
	 * dialog (kitecorp/transferify-ui#3197), whose 72 hours are named as a day rather than a clock. Takes
	 * the countdown's line; the two never arrive together.
	 */
	endsOn?: string;
}) {
	const {offerLadder, pricing} = useMessages();
	const {locale} = useLocale();
	const {amount, note} = firstMonthPresentation(gift.currency, gift.firstMonth, gift.renewal, locale, pricing.welcome.thenMonthly);
	const intro = gift.intro;
	const plan = pricing.planNames[gift.targetPlan];
	return <PeelTicket
		title={intro === undefined ? offerLadder.gift.title(amount, note)
			: offerLadder.gift.introTitle(intro.days, plan, amount, formatMoney(gift.currency, gift.renewal, locale))}
		value={amount} terms={intro === undefined ? pricing.welcome.firstMonth : offerLadder.gift.introTerms(intro.days)}
		eyebrow={eyebrow ?? (intro === undefined ? undefined : offerLadder.gift.introEyebrow)}
		compactValue stubLabel={offerLadder.gift.planLabel(plan)}
		claimed={claimed} onClaim={onClaim} disabled={countdown?.expired ?? false} stub={stub}
		metadata={intro !== undefined ? null : <>
			{discountsLeft !== undefined && <span>{offerLadder.gift.discountsLeft(discountsLeft)}</span>}
			{countdown?.text != null && <span data-testid="ladder-gift-expiry" role={countdown.expired ? "status" : undefined}>
				{countdown.expired ? offerLadder.gift.expired : offerLadder.gift.endsIn(countdown.text)}
			</span>}
			{countdown === undefined && endsOn !== undefined && <span data-testid="ladder-gift-expiry">
				{offerLadder.limitWall.endsOn(endsOn)}
			</span>}
		</>}
	/>;
}
